import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { TreeViewBaseItem } from '@mui/x-tree-view/models';
import SearchIcon from '@mui/icons-material/Search';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Panel from './Panel';
import { TreeItem, TreeItemProps } from '@mui/x-tree-view';
import { locationService, LocationNode, LocationTreeResponse,AUTH_ERRORS } from '../services/axiosService';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import { Application } from '../constants/Application';
import { trackEvent } from '../services/trackEvent';

interface LocationSelectorProps {
  selectedLocations?: string[];
  onLocationSelect?: (locations: string[], locationDetails?: Array<{id: string, name: string}>) => void;
  multiSelect?: boolean;
  searchable?: boolean;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  applicationName?: string; // Optional application name override
  expandSelectedNodes?: boolean; // New prop to expand nodes containing selected locations
}

export default function LocationSelector({
  selectedLocations = [],
  onLocationSelect = () => {},
  multiSelect = false,
  searchable = false,
  required = false,
  placeholder = 'Select locations...',
  disabled = false,
  applicationName,
  expandSelectedNodes = false
}: LocationSelectorProps) {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const { getAccount } = useUser();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [locations, setLocations] = useState<TreeViewBaseItem[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<TreeViewBaseItem[]>([]);
  const [selected, setSelected] = useState<string[]>(selectedLocations);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>(['-1']); // Always expand root by default
  const [preSearchExpandedItems, setPreSearchExpandedItems] = useState<string[]>(['-1']); // Store expansion state before search

  // Debounced search function
  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  // Helper function to find all parent nodes of selected items
  const findParentNodes = useCallback((items: TreeViewBaseItem[], selectedIds: string[]): string[] => {
    const allNodesToExpand = new Set<string>();
    
    // Function to find a node by ID and return the path to it
    const findNodePath = (nodes: TreeViewBaseItem[], targetId: string, currentPath: string[] = []): string[] | null => {
      for (const node of nodes) {
        const newPath = [...currentPath, node.id];
        
        // If this is the target node, return the path
        if (node.id === targetId) {
          return newPath;
        }
        
        // Search in children
        if (node.children && node.children.length > 0) {
          const childPath = findNodePath(node.children, targetId, newPath);
          if (childPath) {
            return childPath;
          }
        }
      }
      return null;
    };
    
    // For each selected location, find its path and add all parent nodes to expand
    selectedIds.forEach(selectedId => {
      const pathToNode = findNodePath(items, selectedId);
      if (pathToNode) {
        // Add all nodes in the path except the last one (the selected node itself)
        // We want to expand all parents, not necessarily the selected node
        pathToNode.slice(0, -1).forEach(nodeId => {
          allNodesToExpand.add(nodeId);
        });
      }
    });
    
    return Array.from(allNodesToExpand);
  }, []);

  // Calculate expanded items based on selected locations
  useEffect(() => {
    if (expandSelectedNodes && selectedLocations.length > 0 && locations.length > 0) {
      const parentNodes = findParentNodes(locations, selectedLocations);
      const allExpandedItems = ['-1', ...parentNodes]; // Always include root
      setExpandedItems(allExpandedItems);
    } else {
      setExpandedItems(['-1']); // Only expand root by default
    }
  }, [locations, expandSelectedNodes, findParentNodes]);

  // Helper function to find all nodes that match the search term and build their paths
  const findMatchingNodesWithPaths = useCallback((nodes: TreeViewBaseItem[], term: string, currentPath: TreeViewBaseItem[] = []): Array<{node: TreeViewBaseItem, path: TreeViewBaseItem[]}> => {
    const results: Array<{node: TreeViewBaseItem, path: TreeViewBaseItem[]}> = [];
    const lowerTerm = term.toLowerCase();
    
    for (const node of nodes) {
      const newPath = [...currentPath, node];
      
      // Check if current node matches
      if (node.label.toString().toLowerCase().includes(lowerTerm)) {
        results.push({ node, path: newPath });
      }
      
      // Recursively search children
      if (node.children && node.children.length > 0) {
        const childResults = findMatchingNodesWithPaths(node.children, term, newPath);
        results.push(...childResults);
      }
    }
    
    return results;
  }, []);

  // Helper function to build a tree structure from paths
  const buildTreeFromPaths = useCallback((pathsData: Array<{node: TreeViewBaseItem, path: TreeViewBaseItem[]}>) => {
    if (pathsData.length === 0) return [];
    
    const treeMap = new Map<string, TreeViewBaseItem>();
    const expandedNodes = new Set<string>();
    
    // Process each path
    pathsData.forEach(({ node, path }) => {
      // Build the complete path in the tree
      for (let i = 0; i < path.length; i++) {
        const currentNode = path[i];
        const nodeId = currentNode.id;
        
        if (!treeMap.has(nodeId)) {
          // Create a new node with empty children initially
          treeMap.set(nodeId, {
            id: currentNode.id,
            label: currentNode.label,
            children: []
          });
        }
        
        // Add to expanded nodes (except the leaf matching node)
        if (i < path.length - 1) {
          expandedNodes.add(nodeId);
        }
        
        // Link parent to child
        if (i > 0) {
          const parentId = path[i - 1].id;
          const parent = treeMap.get(parentId);
          const child = treeMap.get(nodeId);
          
          if (parent && child) {
            // Only add child if it's not already present
            if (!parent.children?.some(c => c.id === child.id)) {
              parent.children = parent.children || [];
              parent.children.push(child);
            }
          }
        }
      }
    });
    
    // Update expanded items to show all paths
    setExpandedItems(Array.from(expandedNodes));
    
    // Return only root nodes
    const rootNodes = Array.from(treeMap.values()).filter(node =>
      !pathsData.some(({ path }) =>
        path.length > 1 && path.slice(1).some(p => p.id === node.id)
      )
    );
    
    return rootNodes;
  }, []);

  // Filter locations based on search term
  const filterLocations = useCallback((term: string) => {
    if (!term) {
      setFilteredLocations(locations);
      setExpandedItems(preSearchExpandedItems); // Restore pre-search expansion state
      return;
    }

    // Store current expansion state before searching (only if we're starting a new search)
    if (filteredLocations === locations) {
      setPreSearchExpandedItems(expandedItems);
    }

    // Find all matching nodes with their complete paths
    const matchingPaths = findMatchingNodesWithPaths(locations, term);
    
    if (matchingPaths.length === 0) {
      setFilteredLocations([]);
      setExpandedItems([]);
      return;
    }
    
    // Build filtered tree showing only paths to matching nodes
    const filteredTree = buildTreeFromPaths(matchingPaths);
    setFilteredLocations(filteredTree);
  }, [locations, findMatchingNodesWithPaths, buildTreeFromPaths, filteredLocations, expandedItems, preSearchExpandedItems]);

  // Create debounced version of filterLocations
  const debouncedFilterLocations = useCallback(
    debounce((term: string) => filterLocations(term), 300),
    [filterLocations]
  );

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value;
    setSearchTerm(term);
    debouncedFilterLocations(term);
  };


  // Helper function to find a location by ID in the tree structure
  const findLocationById = useCallback((nodes: TreeViewBaseItem[], targetId: string): TreeViewBaseItem | null => {
    for (const node of nodes) {
      if (node.id === targetId) {
        return node;
      }
      if (node.children && node.children.length > 0) {
        const found = findLocationById(node.children, targetId);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }, []);

  // Handle selection change
  const handleSelectionChange = (
    event: React.SyntheticEvent | null,
    itemIds: string[],
  ) => {
  
  // Get location details for the selected IDs
    const selectedLocationDetails = itemIds.map(locationId => {
        const location = locations.length > 0 ? findLocationById(locations[0].children || [], locationId) : null;
      return {
        id: locationId,
        name: location?.label || t('locationSelector.unknownLocation')
      };
    });
    
    // Track location selection
    if (itemIds.length > 0) {
      itemIds.forEach(locationId => {
          const location = locations.length > 0 ? findLocationById(locations[0].children || [], locationId) : null;
          trackEvent.locationSelected(locationId, location?.label || t('locationSelector.unknownLocation'), {
          multiSelect: multiSelect,
          totalSelected: itemIds.length,
          applicationName: applicationName || 'Unknown'
        });
      });
    }
  
  
    setSelected(itemIds);
    onLocationSelect(itemIds, selectedLocationDetails);
    
    if (required && itemIds.length === 0) {
      setError(t('locationSelector.selectAtLeastOne'));
    } else {
      setError(null);
    }
  };

  const handleExpandedItemsChange = (
    event: React.SyntheticEvent | null,
    itemIds: string[],
  ) => {
    setExpandedItems(itemIds);
  };

  // Helper function to convert LocationNode to TreeViewBaseItem
  const convertLocationNodeToTreeViewItem = (node: LocationNode): TreeViewBaseItem => {
    return {
      id: node.locationId.toString(),
      label: node.name,
      children: node.children.map(child => convertLocationNodeToTreeViewItem(child))
    };
  };

  // Fetch location data from API
  useEffect(() => {
    const fetchLocationData = async () => {
      setLoading(true);
      setLoadError(null);
      
      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          throw new Error(AUTH_ERRORS.NO_ACCESS_TOKEN);
        }
        
        // Get the application name (use prop or default to Protect)
        const appName = applicationName || Application.Protect;
        
        // Get account for the application
        const account = getAccount(appName);
        if (!account || !account.accountId) {
          throw new Error(`No account found for application: ${appName}`);
        }
        
        // Fetch location tree data
        const response = await locationService.getLocationTreeByAccount(accessToken, account.accountId);
        
        // Create a single root node with all locations as children
        const rootNode: TreeViewBaseItem = {
          id: '-1',
          label: response.rootNodeName || t('locationSelector.allLocations'),
          children: response.locations.map(location =>
            convertLocationNodeToTreeViewItem(location)
          )
        };
        
        // Use an array with just the root node
        const treeViewItems: TreeViewBaseItem[] = [rootNode];
        
        setLocations(treeViewItems);
        setFilteredLocations(treeViewItems);
      } catch (error) {
        console.error('Failed to fetch location data:', error);
        setLoadError(error instanceof Error ? error.message : t('locationSelector.loadError'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchLocationData();
  }, [applicationName]);
  
  // Update selected state when selectedLocations prop changes
  useEffect(() => {
    setSelected(selectedLocations);
  }, [selectedLocations]);

  const CustomCheckbox = React.forwardRef(function CustomCheckbox(
    props: React.InputHTMLAttributes<HTMLInputElement>,
    ref: React.Ref<HTMLInputElement>,
  ) {
    return (
      <Box sx={{ display: 'flex', alignSelf: 'flex-start', pt: 0.1 }}>
        <input type="checkbox" ref={ref} {...props} />
      </Box>
    );
  });


  const CustomTreeItem = React.forwardRef(function CustomTreeItem(
    props: TreeItemProps,
    ref: React.Ref<HTMLLIElement>,
  ) {
    return (
      <TreeItem
        {...props}
        ref={ref}
        slots={{
          checkbox: CustomCheckbox,
        }}
      />
    );
  });

  return (
    <FormControl error={!!error} fullWidth>
      <Box
        sx={{
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          position: 'relative'
        }}
      >
        <Panel
          heading={t('locationSelector.title')}
          subText={!required ? t('locationSelector.optional') : ""}
          width={300}
          height={500}
        >
        {/* Search Box */}
        {searchable && (
          <Box sx={{ p: 1, flexShrink: 0 }}>
            <Paper
              sx={{
                p: '2px 4px',
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #e0e0e0',
                boxShadow: 'none'
              }}
            >
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder={t('locationSelector.searchPlaceholder')}
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
                <SearchIcon />
              </IconButton>
            </Paper>
          </Box>
        )}
        
        {/* Tree View */}
        <Box sx={{
          // Calculate tree height based on panel height (420) minus header (48px) and search box if present (~50px)
          height: searchable ? 'calc(100% - 114px)' : 'calc(100% - 48px)',
          overflow: 'auto',
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: loading ? 'center' : 'flex-start',
          alignItems: loading ? 'center' : 'stretch',
        }}>
          {loading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
              <CircularProgress size={40} />
              <Box sx={{ mt: 2 }}>{t('common.loadingLocations')}</Box>
            </Box>
          )}
          
          {loadError && !loading && (
            <Alert severity="error" sx={{ m: 2 }}>
              {loadError}
            </Alert>
          )}
          
          {!loading && !loadError && filteredLocations.length === 0 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              {t('locationSelector.noLocationsFound')}
            </Box>
          )}
          
          {!loading && !loadError && filteredLocations.length > 0 && (
            <RichTreeView
              multiSelect
              checkboxSelection={multiSelect}
              items={filteredLocations}
              expandedItems={expandedItems}
              onExpandedItemsChange={handleExpandedItemsChange}
              selectedItems={selected}
              aria-label={placeholder}
              onSelectedItemsChange={handleSelectionChange}
              // slots={{ item: CustomTreeItem }}
              sx={{
                '& .MuiTreeItem-content': {
                  py: 0.5,
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start', // Align items to the top
                },
                '& .MuiTreeItem-label': {
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  display: 'block', // Ensure label takes full width when wrapping
                },
                // Style all checkboxes to be smaller
                '& .MuiCheckbox-root': {
                  padding: '4px',
                  '& svg': {
                    fontSize: '1rem', // Smaller checkbox icon (default is 1.5rem)
                  },
                },
                // Style indeterminate checkboxes to look like unchecked
                '& .MuiCheckbox-indeterminate': {
                  color: 'rgba(0, 0, 0, 0.54) !important',
                  padding: '4px',
                  '& svg': {
                    fontSize: '1rem',
                    display: 'none !important',
                  },
                  '&::after': {
                    content: '""',
                    width: '16px', // Smaller size to match the reduced checkbox
                    height: '16px',
                    top: '4px',
                    left: '4px',
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3e%3cpath d='M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z' fill='rgba(0,0,0,0.54)'/%3e%3c/svg%3e")`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    pointerEvents: 'none',
                  }
                },
              }}
            />
          )}
        </Box>
      </Panel>
      </Box>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
}
